/**
 * Typed constants for every xPT AG Grid cell class. Use these in column defs
 * instead of string literals so typos are caught at compile time and renames
 * are safe.
 *
 * Example: `{ cellClass: AG_CELL_CLASSES.editable }` instead of `{ cellClass: 'ag-cell-editable' }`
 */
export const AG_CELL_CLASSES = {
  // state
  editable: 'ag-cell-editable',
  error: 'ag-cell-error',
  warning: 'ag-cell-warning',
  modified: 'ag-cell-modified',
  readonly: 'ag-cell-readonly',
  /** Row-level variant: apply via rowClassRules to grey out every cell of a read-only row. */
  readonlyRow: 'ag-row-readonly',
  /**
   * Row-level advisory marker: apply via rowClassRules to draw a left-edge bar on a row needing
   * attention. Used instead of a background tint where the grid's cells are already tinted (e.g. the
   * editable-cell affordance), because a shape cue stays legible when hues collide.
   */
  warningRow: 'ag-row-warning',
  /** Row-level error marker: applied via rowClassRules for rows that failed validation. */
  errorRow: 'ag-row-error',

  // layout utilities
  bold: 'ag-cell-bold',
  center: 'ag-cell-center',
  right: 'ag-cell-right',
  wrap: 'ag-cell-wrap',
  numeric: 'ag-cell-numeric',
  required: 'ag-cell-required',

  // time period
  historical: 'ag-cell-historical',
  historicalTotal: 'ag-cell-historical-total',
  projected: 'ag-cell-projected',
  projectedTotal: 'ag-cell-projected-total',
  grandTotal: 'ag-cell-grand-total',

  // variance
  variance: 'ag-cell-variance',
  variancePositive: 'ag-cell-variance-positive',
  varianceNegative: 'ag-cell-variance-negative',
  varianceNeutral: 'ag-cell-variance-neutral',

  // header
  headerCenter: 'ag-header-center',
  headerAccentPrimary: 'ag-header-accent-primary',
  headerAccentSecondary: 'ag-header-accent-secondary',

  // Excel export tags (no visual CSS — just tag cells for Excel styling)
  /** Marks cells ignored when a workbook is uploaded. */
  exportIgnored: 'ag-cell-export-ignored',
  exportText: 'ag-cell-export-text',
  exportNumber: 'ag-cell-export-number',
  exportBoolean: 'ag-cell-export-boolean',
  exportRequired: 'ag-cell-export-required',
  /** Export-only fill for cells that cannot be edited in the workbook's source grid. */
  exportReadonly: 'ag-cell-export-readonly',
  /** Export-only numeric formats selected per cell. */
  exportNumberWhole: 'ag-cell-export-number-whole',
  exportNumberOneDecimal: 'ag-cell-export-number-one-decimal',
  exportNumberTwoDecimals: 'ag-cell-export-number-two-decimals'
} as const;

export type AgCellClass = (typeof AG_CELL_CLASSES)[keyof typeof AG_CELL_CLASSES];
